import { ThemeEvents } from "@theme/events";

// ============================================
// CONFIGURATION
// ============================================
const CONFIG = {
  threshold: 2500, // cents (3000 = £30.00)
  progressMessage: "Add __AMOUNT__ more for Free Shipping",
  successMessage: "🎉 You've unlocked Free Shipping!",
  fallbackCurrencySymbol: "£",
};

// ============================================
// STYLES
// ============================================
const STYLES = `
  .shipping-progress-bar {
    display: block;
    padding: 0.75rem 1rem;
  }
  .shipping-progress-bar[hidden] {
    display: none;
  }
  .shipping-progress-bar__message {
    text-align: center;
    font-size: var(--font-size--sm, 0.875rem);
    font-family: 'Gotham Medium', var(--font-family--body, sans-serif);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.4rem;
    color: var(--color--foreground, #000);
  }
  .shipping-progress-bar__message--success {
    color: #6F9AA3;
    font-weight: 600;
  }
  .shipping-progress-bar__track {
    height: 8px;
    background: #f5f5f5;
    border: 1px solid #6F9AA3;
    border-radius: 8px;
    overflow: hidden;
  }
  .shipping-progress-bar__fill {
    height: 100%;
    width: var(--progress, 0%);
    background: #6F9AA3;
    border-radius: 8px;
    transition: width 0.4s ease;
  }
`;

if (!document.getElementById("shipping-progress-bar-styles")) {
  const style = document.createElement("style");
  style.id = "shipping-progress-bar-styles";
  style.textContent = STYLES;
  document.head.appendChild(style);
}

// ============================================
// STATE
// ============================================
let lastKnownCartTotal = null;

// ============================================
// HELPERS
// ============================================

function formatMoney(cents) {
  if (window.Shopify?.formatMoney) {
    const fmt =
      window.money_format ||
      window.Shopify.money_format ||
      `${CONFIG.fallbackCurrencySymbol}{{amount}}`;
    return window.Shopify.formatMoney(cents, fmt);
  }
  return `${CONFIG.fallbackCurrencySymbol}${(cents / 100).toFixed(2)}`;
}

/**
 * Fetch the true cart total directly from Shopify.
 * This is the single source of truth.
 */
function fetchCartTotal() {
  return fetch("/cart.js")
    .then((r) => r.json())
    .then((cart) => {
      if (typeof cart.total_price === "number") {
        return cart.total_price;
      }
      return null;
    })
    .catch(() => null);
}

// ============================================
// CREATE / FIND THE BAR
// ============================================

function createBarElement() {
  const bar = document.createElement("div");
  bar.className = "shipping-progress-bar";
  bar.setAttribute("data-shipping-bar", "");
  bar.hidden = true;
  bar.innerHTML = `
    <p class="shipping-progress-bar__message"></p>
    <div class="shipping-progress-bar__track">
      <div class="shipping-progress-bar__fill" role="progressbar" aria-valuemin="0" aria-valuemax="100"></div>
    </div>
  `;
  return bar;
}

function getOrInjectBar() {
  const existing = document.querySelector("[data-shipping-bar]");
  if (existing) return existing;

  const bar = createBarElement();

  const drawerContent = document.querySelector(
    ".cart-drawer__content .cart-drawer__items"
  );
  if (drawerContent && drawerContent.parentNode) {
    drawerContent.parentNode.insertBefore(bar, drawerContent);
    return bar;
  }

  const header = document.querySelector(".cart-drawer__header");
  if (header && header.parentNode) {
    header.parentNode.insertBefore(bar, header.nextSibling);
    return bar;
  }

  return null;
}

// ============================================
// UPDATE
// ============================================

function updateBar(cartTotal) {
  const bar = getOrInjectBar();
  if (!bar) return;

  const messageEl = bar.querySelector(".shipping-progress-bar__message");
  const fillEl = bar.querySelector(".shipping-progress-bar__fill");
  const threshold = CONFIG.threshold;

  if (!threshold || threshold <= 0 || cartTotal === 0) {
    bar.hidden = true;
    return;
  }

  bar.hidden = false;

  const remaining = threshold - cartTotal;
  const pct = Math.min(100, (cartTotal / threshold) * 100);
  const reached = remaining <= 0;

  if (fillEl) {
    fillEl.style.setProperty("--progress", `${pct}%`);
    fillEl.setAttribute("aria-valuenow", String(Math.round(pct)));
  }

  if (messageEl) {
    if (reached) {
      messageEl.textContent = CONFIG.successMessage;
      messageEl.classList.add("shipping-progress-bar__message--success");
    } else {
      messageEl.textContent = CONFIG.progressMessage.replace(
        "__AMOUNT__",
        formatMoney(remaining)
      );
      messageEl.classList.remove("shipping-progress-bar__message--success");
    }
  }
}

/**
 * Update from the event's total_price, then verify against /cart.js.
 * This handles cases where the event carries a stale or missing total.
 */
function handleCartEvent(event) {
  const eventTotal = event.detail?.resource?.total_price;

  // Immediately update if we have a total on the event
  if (typeof eventTotal === "number") {
    lastKnownCartTotal = eventTotal;
    updateBar(eventTotal);
  }

  // Always verify against /cart.js after a short delay
  // (gives the Shopify backend time to settle)
  setTimeout(() => {
    fetchCartTotal().then((apiTotal) => {
      if (apiTotal !== null && apiTotal !== lastKnownCartTotal) {
        lastKnownCartTotal = apiTotal;
        updateBar(apiTotal);
      }
    });
  }, 300);
}

// ============================================
// EVENT LISTENERS
// ============================================

// cart:update covers CartAddEvent + CartUpdateEvent
document.addEventListener(ThemeEvents.cartUpdate, handleCartEvent);
// discount:update is separate
document.addEventListener(ThemeEvents.discountUpdate, handleCartEvent);

// Re-inject after morphs wipe the bar
const morphObserver = new MutationObserver(() => {
  if (!document.querySelector("[data-shipping-bar]")) {
    if (lastKnownCartTotal !== null && lastKnownCartTotal > 0) {
      updateBar(lastKnownCartTotal);
    }
  }
});

// Re-render when cart drawer opens
document.addEventListener("dialog:open", (event) => {
  if (event.target?.closest?.("cart-drawer-component")) {
    setTimeout(() => {
      fetchCartTotal().then((apiTotal) => {
        if (apiTotal !== null) {
          lastKnownCartTotal = apiTotal;
          updateBar(apiTotal);
        }
      });
    }, 50);
  }
});

// ============================================
// INIT
// ============================================

function init() {
  fetchCartTotal().then((apiTotal) => {
    if (apiTotal !== null && apiTotal > 0) {
      lastKnownCartTotal = apiTotal;
      updateBar(apiTotal);
    }
  });

  const cartContainer = document.querySelector("cart-items-component");
  if (cartContainer) {
    morphObserver.observe(cartContainer, {
      childList: true,
      subtree: true,
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}